import React, { useCallback, useEffect, useState } from "react";
import { MemoryRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { ROUTES } from "./consts";
import { SelectDateTime } from "./components/MainTrack";
import { MiniSelectDateTime } from "./components/MiniTrack";
import JuniorTrack from "./components/JuniorTrack";
import { VRSelectTimeslots } from "./components/VirtualReality";
import { IGVariant, TTrackTypes } from "./types";
import { useTrackContext } from "./hooks/useTrackContext";
import { INITIAL_STATE } from "./hooks/useReducerState";
import BattleKartTrack from "./components/battleKart";

const styles = {
  fontFamily: "'Montserrat', sans-serif",
};

const ProductSelectorApp = () => {
  return (
    <div className="" style={styles}>
      <MemoryRouter>
        <AppWithNavigation />
      </MemoryRouter>
    </div>
  );
};

const AppWithNavigation = () => {
  const navigate = useNavigate();
  const { dispatch } = useTrackContext();
  const [selectedTrack, setSelectedTrack] = useState<TTrackTypes>("MAIN_TRACK");

  const onVariantSelection = useCallback((isAdded: boolean, variant: IGVariant, selectedVariants: IGVariant[]) => {
    console.log("[Variant Selection] ", isAdded, variant, selectedVariants);
    // dispatch event to notify about the variant selection
    window.dispatchEvent(
      new CustomEvent<TVariantSelectionEvent>("hk_product_component", {
        detail: {
          subType: "change_variant_selection",
          data: {
            isAdded,
            variant: variant,
            variants: selectedVariants,
          },
        },
      })
    );
  }, []);

  const onTrackChange = useCallback(
    (event: CustomEvent<TIncomingEvents>) => {
      console.log("[Custom Event] ", event?.type, event?.detail);
      if (event && event.type === "hk_product_component_listener") {
        switch (event?.detail?.subType) {
          case "change_track_selection":
            if (event?.detail?.data?.track) {
              const track = parseTrack(event.detail.data.track);
              if (track) {
                setSelectedTrack(track);
                dispatch({
                  type: "SET_SELECTED_TRACK",
                  payload: {
                    selectedTrackTitle: track,
                    trackBanner: "",
                  },
                });
                // Navigate programmatically to the new track
                navigate(ROUTES[track].DATE_TIME);
              }
            }
            break;
          case "change_selected_variants":
            if (event?.detail?.data) {
              const { MAIN_TRACK, JUNIOR_TRACK, MINI_TRACK, VR, BATTLE_KART } = event.detail.data;
              if (Array.isArray(MAIN_TRACK)) {
                dispatch({
                  type: "SET_DATE_AND_VARIANT",
                  payload: {
                    variantsAndDate: {
                      ...INITIAL_STATE.mainTrackSelection.variantsAndDate,
                      selectedVariants: MAIN_TRACK.map((variant) => ({
                        variantSelectedTime: new Date(),
                        variantsSelectedObject: variant,
                      })),
                    },
                  },
                });
              }
              if (Array.isArray(JUNIOR_TRACK)) {
                dispatch({
                  type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
                  payload: {
                    variantsAndDate: {
                      ...INITIAL_STATE.juniorTrackSelection.variantsAndDate,
                      selectedVariants: JUNIOR_TRACK.map((variant) => ({
                        variantSelectedTime: new Date(),
                        variantsSelectedObject: variant,
                      })),
                    },
                  },
                });
              }
              if (Array.isArray(MINI_TRACK)) {
                dispatch({
                  type: "SET_DATE_AND_VARIANT_MINI_TRACK",
                  payload: {
                    variantsAndDate: {
                      ...INITIAL_STATE.miniTrackSection.variantsAndDate,
                      selectedVariants: MINI_TRACK.map((variant) => ({
                        variantSelectedTime: new Date(),
                        variantsSelectedObject: variant,
                      })),
                    },
                  },
                });
              }
              if (Array.isArray(VR)) {
                dispatch({
                  type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
                  payload: {
                    variantsAndDate: {
                      ...INITIAL_STATE.vrSelection.variantsAndDate,
                      selectedVariants: VR.map((variant) => ({
                        variantSelectedTime: new Date(),
                        variantsSelectedObject: variant,
                      })),
                    },
                  },
                });
              }
              if (Array.isArray(BATTLE_KART)) {
                dispatch({
                  type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
                  payload: {
                    variantsAndDate: {
                      ...INITIAL_STATE.battleKartSelection.variantsAndDate,
                      selectedVariants: BATTLE_KART.map((variant) => ({
                        variantSelectedTime: new Date(),
                        variantsSelectedObject: variant,
                      })),
                    },
                  },
                });
              }
            }
            break;
          default:
            console.log("[Unhandled Custom Event] ", event);
            break;
        }
      }
    },
    [navigate, dispatch]
  );

  // mainTrackSelection, miniTrackSection, juniorTrackSelection, vrSelection

  useEffect(() => {
    console.log("effect...");
    dispatch({
      type: "SET_RACES_SEATS",
      payload: {
        selectedTrack: "MAIN_TRACK",
        racesSeats: {
          numberOfRaces: Infinity,
          numberOfSeats: Infinity,
        },
      },
    });
    dispatch({
      type: "SET_RACES_SEATS_JUNIOR_TRACK",
      payload: {
        racesSeats: {
          numberOfRaces: Infinity,
          numberOfSeats: Infinity,
        },
      },
    });
    dispatch({
      type: "SET_RACES_SEATS_MINI_TRACK",
      payload: {
        selectedTrack: "MINI_TRACK",
        racesSeats: {
          numberOfRaces: Infinity,
          numberOfSeats: Infinity,
        },
      },
    });
    dispatch({
      type: "SET_PLAYERS_SESSIONS_VIRTUAL_REALITY",
      payload: {
        playersAndSessions: {
          numberOfPlayers: Infinity,
          numberOfSessions: Infinity,
        },
      },
    });
    dispatch({
      type: "SET_RACES_SEATS_BATTLE_KART_TRACK",
      payload: {
        racesSeats: {
          numberOfRaces: Infinity,
          numberOfSeats: Infinity,
        },
      },
    });
    window.addEventListener("hk_product_component_listener", onTrackChange as EventListener);
    return () => {
      window.removeEventListener("hk_product_component_listener", onTrackChange as EventListener);
    };
  }, [onTrackChange, dispatch]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("hk_product_component", { detail: { subType: "product_component_ready" } }));
  }, []);

  return (
    <Routes>
      <Route index path={ROUTES.INDEX} element={<TrackSelection selectedTrack={selectedTrack} />} />
      <Route path={ROUTES.MAIN_TRACK.DATE_TIME} element={<SelectDateTime selectProductOnly={true} onVariantSelection={onVariantSelection} />} />
      <Route path={ROUTES.JUNIOR_TRACK.DATE_TIME} element={<JuniorTrack selectProductOnly={true} onVariantSelection={onVariantSelection} />} />
      <Route path={ROUTES.MINI_TRACK.DATE_TIME} element={<MiniSelectDateTime selectProductOnly={true} onVariantSelection={onVariantSelection} />} />
      <Route path={ROUTES.VIRTUAL_REALITY.DATE_TIME} element={<VRSelectTimeslots selectProductOnly={true} onVariantSelection={onVariantSelection} />} />
      <Route path={ROUTES.BATTLE_KART_TRACK.DATE_TIME} element={<BattleKartTrack selectProductOnly={true} onVariantSelection={onVariantSelection} />} />
    </Routes>
  );
};

const TrackSelection = ({ selectedTrack }: { selectedTrack: TTrackTypes }) => {
  console.log("selected track", selectedTrack, ROUTES[selectedTrack].DATE_TIME, ROUTES.BATTLE_KART_TRACK.DATE_TIME);
  return <Navigate to={ROUTES[selectedTrack].DATE_TIME} />;
};

// parse hk-app tracks to product component tracks
const parseTrack = (track: TIncomingTrack): TTrackTypes | null => {
  switch (track) {
    case "MAIN_TRACK":
      return "MAIN_TRACK";
    case "JUNIOR_TRACK":
      return "JUNIOR_TRACK";
    case "MINI_TRACK":
      return "MINI_TRACK";
    case "VR":
      return "VIRTUAL_REALITY";
    case "BATTLE_KART":
      return "BATTLE_KART_TRACK";
    default:
      return null;
  }
};

type TIncomingTrack = "MAIN_TRACK" | "JUNIOR_TRACK" | "MINI_TRACK" | "VR" | "BATTLE_KART";

type TVariantSelectionEvent = { subType: "change_variant_selection"; data: { variant: IGVariant; isAdded: boolean; variants: IGVariant[] } };

type TIncomingEvents =
  | { subType: "change_track_selection"; data: { track: TIncomingTrack } }
  | { subType: "change_selected_variants"; data: { [key in Partial<TIncomingTrack>]: IGVariant[] } };

export default ProductSelectorApp;
